/**
 * WatermarkedMedia — wraps images and videos with a tiled, semi-transparent
 * overlay showing the logged-in user's email. If a leaked screenshot or
 * recording ever surfaces, we can trace it back to the offending account.
 *
 * Admin / support_staff are exempt — they see clean images for QA.
 *
 * The watermark is rendered as an SVG <pattern> tile so it survives most
 * common image-processing attempts (cropping moves it but doesn't remove
 * it across the frame; rotating / mirroring still keeps it visible
 * somewhere; flattening to JPEG bakes it in).
 *
 * Usage:
 *   <WatermarkedMedia>
 *     <img src={..} />
 *   </WatermarkedMedia>
 */
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';

export function WatermarkedMedia({ children, className = '', mark = null }) {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  // Bypass watermark for admin / support_staff so QA + content-management
  // workflows show pristine media. Otherwise build a stable watermark
  // string the user can be traced by.
  const exempt = role === 'admin' || role === 'support_staff';
  if (exempt || !user) {
    return <div className={className}>{children}</div>;
  }

  // Composite mark = email + last 4 of user_id so we can match against the
  // users collection even if email is suspected to be spoofed.
  const computed = mark || `${user.email || 'guest'} · ${(user.user_id || '').slice(-6)}`;

  // Inline the SVG as a data: URL so it doesn't trigger an extra request.
  // The pattern repeats every 280×280px to ensure at least one full text
  // appears on every smartphone-sized screenshot region.
  const svg = encodeURIComponent(`
    <svg xmlns='http://www.w3.org/2000/svg' width='280' height='280' viewBox='0 0 280 280'>
      <defs>
        <style>.gv-wm{font-family:Inter,system-ui,sans-serif;font-size:14px;fill:rgba(15,23,42,0.18);font-weight:500;}</style>
      </defs>
      <g transform='rotate(-22 140 140)'>
        <text x='10'  y='80'  class='gv-wm'>GurukulVeda · ${computed}</text>
        <text x='10'  y='180' class='gv-wm'>GurukulVeda · ${computed}</text>
      </g>
    </svg>
  `);
  const dataUrl = `url("data:image/svg+xml;utf8,${svg}")`;

  return (
    <div className={`relative ${className}`} data-testid="watermarked-media">
      {children}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none z-10"
        style={{
          backgroundImage: dataUrl,
          backgroundRepeat: 'repeat',
          mixBlendMode: 'multiply',
        }}
      />
    </div>
  );
}
